import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import axios from 'axios';
import productPlaceholder from '../images/Home/product2.webp';

function ManageProducts() {
    const navigate = useNavigate();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const apiLocalUrl = process.env.REACT_APP_API_LOCAL_URL;
    const [activeTab, setActiveTab] = useState('ecommerce'); // 'ecommerce' or 'all'
    const [products, setProducts] = useState([]);
    const [imageMap, setImageMap] = useState({});
    const [searchItem, setSearchItem] = useState('');
    const [loading, setLoading] = useState(false);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const endpoint = activeTab === 'ecommerce'
                ? `${apiBaseUrl}/getEcommerceProducts/BLACKBATON_2526`
                : `${apiBaseUrl}/getAllProductsForEcom/BLACKBATON_2526`;
            const response = await fetch(endpoint);
            const data = await response.json();
            setProducts(data);

            // Prefetch images using backend's image resolution logic
            const imagePromises = data.map(async (item) => {
                try {
                    const res = await axios.get(`${apiBaseUrl}/getFirstAvailableImage/BLACKBATON_2526`, {
                        params: { itemId: item.slno, displayFirst: item.DisplayFirst || item.displayFirst || '' }
                    });
                    return { id: item.slno, image: res.data.image };
                } catch (e) {
                    return { id: item.slno, image: null };
                }
            });

            const resolved = await Promise.all(imagePromises);
            setImageMap(resolved.reduce((map, cur) => {
                map[cur.id] = cur.image;
                return map;
            }, {}));
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [activeTab]);

    const handleUpdateField = async (slno, field, currentValue) => {
        try {
            // For boolean-like fields, toggle 1 and 0 (or null for noSize)
            let newValue;
            if (field === 'noSize') {
                newValue = currentValue === null ? 1 : null;
            } else {
                newValue = currentValue === 1 ? 0 : 1;
            }

            const response = await axios.post(`${apiBaseUrl}/updateProductField/BLACKBATON_2526`, {
                slno,
                field,
                value: newValue
            });

            if (response.data.success) {
                setProducts(prev => prev.map(item =>
                    item.slno === slno ? { ...item, [field]: newValue } : item
                ));
            }
        } catch (error) {
            console.error(`Error updating ${field}:`, error);
        }
    };

    const filteredProducts = products.filter(item =>
        item.itemname?.toLowerCase().includes(searchItem.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-dmSans">
            <AdminHeader />

            <div className="flex-grow py-6 lg:py-12 lg:px-12 px-4">
                <div className="max-w-7xl mx-auto">
                    {/* Header Section */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                        <div className="flex flex-col">
                            <h1 className="text-2xl lg:text-3xl font-900 text-gray-900 font-montserrat uppercase tracking-tight">Manage Products</h1>
                            <div className="flex gap-4 mt-4">
                                <button
                                    onClick={() => setActiveTab('ecommerce')}
                                    className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${activeTab === 'ecommerce' ? 'bg-black text-white' : 'bg-white text-gray-400 border border-gray-100 hover:bg-gray-50'}`}
                                >
                                    Ecommerce
                                </button>
                                <button
                                    onClick={() => setActiveTab('all')}
                                    className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${activeTab === 'all' ? 'bg-black text-white' : 'bg-white text-gray-400 border border-gray-100 hover:bg-gray-50'}`}
                                >
                                    All Products
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 bg-white p-2 rounded-2xl shadow-sm border border-gray-100 w-full md:w-auto">
                            <div className="relative flex-grow md:w-64">
                                <input
                                    type="text"
                                    className="w-full bg-gray-50 border-none rounded-xl px-4 py-2.5 pl-10 text-sm font-bold focus:ring-2 focus:ring-black/5 transition-all placeholder:text-gray-300 uppercase tracking-widest"
                                    value={searchItem}
                                    placeholder="Search items..."
                                    onChange={(e) => setSearchItem(e.target.value)}
                                />
                                <svg className="absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            </div>
                        </div>
                    </div>

                    {/* Table View */}
                    <div className="bg-white shadow-2xl rounded-[2rem] border border-gray-100 overflow-hidden overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[800px]">
                            <thead>
                                <tr className="bg-black text-white">
                                    <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em]">SI.No</th>
                                    <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-center">Image</th>
                                    <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em]">Item Name</th>
                                    {activeTab === 'ecommerce' ? (
                                        <>
                                            <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-center">Best Seller</th>
                                            <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-center">Limited Sale</th>
                                            <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-center">New Arrivals</th>
                                            <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-center">Size</th>
                                        </>
                                    ) : (
                                        <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-center">Ecommerce</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan="10" className="px-6 py-12 text-center text-gray-400 font-bold uppercase tracking-widest text-xs">Loading products...</td>
                                    </tr>
                                ) : filteredProducts.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="px-6 py-12 text-center text-gray-400 font-bold uppercase tracking-widest text-xs">No products found</td>
                                    </tr>
                                ) : (
                                    filteredProducts.map((pro, index) => (
                                        <tr key={pro.slno} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-6 text-xs font-black text-gray-400">{index + 1}</td>
                                            <td className="px-6 py-6 text-center border-l border-gray-50/50">
                                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gray-50 overflow-hidden border border-gray-100">
                                                    <img
                                                        src={imageMap[pro.slno]
                                                            ? `${apiLocalUrl}/uploads/${imageMap[pro.slno]}.webp`
                                                            : `${apiLocalUrl}/uploads/${pro.DisplayFirst || pro.displayFirst || pro.slno}.webp`}
                                                        alt={pro.itemname}
                                                        className="w-full h-full object-cover"
                                                        loading="lazy"
                                                        onError={(e) => { e.target.src = productPlaceholder; }}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-6 py-6">
                                                <div className="flex flex-col">
                                                    <span className="text-xs font-900 text-gray-900 font-montserrat uppercase leading-tight">{pro.itemname}</span>
                                                    <span className="text-[9px] font-bold text-gray-300 mt-1 uppercase tracking-tighter">ID: {pro.slno}</span>
                                                </div>
                                            </td>
                                            {activeTab === 'ecommerce' ? (
                                                <>
                                                    <td className="px-4 py-6 text-center">
                                                        <button
                                                            onClick={() => handleUpdateField(pro.slno, 'BestSeller', pro.BestSeller)}
                                                            className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 ${pro.BestSeller === 1 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                                                        >
                                                            {pro.BestSeller === 1 ? 'Enabled' : 'Disabled'}
                                                        </button>
                                                    </td>
                                                    <td className="px-4 py-6 text-center">
                                                        <button
                                                            onClick={() => handleUpdateField(pro.slno, 'LimitedSale', pro.LimitedSale)}
                                                            className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 ${pro.LimitedSale === 1 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                                                        >
                                                            {pro.LimitedSale === 1 ? 'Enabled' : 'Disabled'}
                                                        </button>
                                                    </td>
                                                    <td className="px-4 py-6 text-center">
                                                        <button
                                                            onClick={() => handleUpdateField(pro.slno, 'NewArrivals', pro.NewArrivals)}
                                                            className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 ${pro.NewArrivals === 1 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                                                        >
                                                            {pro.NewArrivals === 1 ? 'Enabled' : 'Disabled'}
                                                        </button>
                                                    </td>
                                                    <td className="px-4 py-6 text-center">
                                                        <button
                                                            onClick={() => handleUpdateField(pro.slno, 'noSize', pro.noSize)}
                                                            className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 ${pro.noSize === 1 ? 'bg-gray-100 text-gray-400' : 'bg-green-100 text-green-600'}`}
                                                        >
                                                            {pro.noSize === 1 ? 'Disabled' : 'Enabled'}
                                                        </button>
                                                    </td>
                                                </>
                                            ) : (
                                                <td className="px-6 py-6 text-center">
                                                    <button
                                                        onClick={() => handleUpdateField(pro.slno, 'isEcom', pro.isEcom)}
                                                        className={`px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95 ${pro.isEcom === 1 ? 'bg-black text-white' : 'bg-gray-100 text-gray-400'}`}
                                                    >
                                                        {pro.isEcom === 1 ? 'Enabled' : 'Enable'}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ManageProducts;
